// src/AppRoutes.tsx
import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import AdminDashboard from "./components/AdminDashboard/AdminDashboard";
import Home from "./components/Home/Home";
import About from "./components/About/About";
import ContactUs from "./components/ContactUs/ContactUs";
import Login from "./components/Login/Login";
import User from "./components/User/User";
import { Opportunities } from "./components/Opportunity/Opportunity";
import PrivacyPolicy from "./components/PrivacyPolicy/PrivacyPolicy";
import { OpportunityForm } from "./components/OpportunityForm/OpportunityForm";
import Layout from "./Layout";
import OpportunityDetail from "./components/OpportunityDetail/OpportunityDetail";
import SmartPicks from "./components/SmartPicks/SmartPicks";
import BlogPage from "./components/BlogPage/BlogPage";
import BlogDetail from "./components/BlogDetail/BlogDetail";
import CreateBlog from "./components/CreateBlog/CreateBlog";
import EditBlog from "./components/EditBlog/EditBlog";
import Sitemap from "./components/Sitemap/Sitemap";
import Terms from "./components/Terms/Terms";
import Cookies from "./components/Cookies/Cookies";
import SaaSLogin from "./components/SaaSLogin/SaaSLogin";
import SaaSDashboard from "./components/SaaSDashboard/SaaSDashboard";
import SaasReviewApplicationsPage from "./components/SaasReviewApplicationsPage/SaasReviewApplicationsPage";
import EmbedTestPage from "./components/PhdConnectEmbed/EmbedTestPage";
import UniversityLogin from "./components/UniversityAuth/UniversityLogin";
import UniversitySignup from "./components/UniversityAuth/UniversitySignup";
import UniversityDashboard from "./components/UniversityAuth/UniversityDashboard";
import UniversityEmailVerification from "./components/UniversityAuth/UniversityEmailVerification";
import UniversityForgotPassword from "./components/UniversityAuth/UniversityForgotPassword";
import UniversityResetPassword from "./components/UniversityAuth/UniversityResetPassword";
import UniversityReviewApplicationsPage from "./components/UniversityAuth/UniversityReviewApplicationsPage";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";

interface AppRoutesProps {
  trackPageView: (path: string) => void; // Define the prop type
}

// Helper function to determine if we're on a SaaS subdomain
const isSaaSSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    // For development, check if we're on a SaaS route
    return window.location.pathname.startsWith('/saas');
  }
  
  // Handle production domains
  if (hostname === 'phdscanner.com' || hostname === 'www.phdscanner.com') {
    return false; // Main domain - show main portal
  }
  
  // Check if it's a subdomain (e.g., app.phdscanner.com, oxford.phdscanner.com)
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www') {
    return true; // Subdomain - show SaaS portal
  }
  
  return false; // Default to main portal
};

// Main Portal Routes (for phdscanner.com and www.phdscanner.com)
const MainPortalRoutes: React.FC<AppRoutesProps> = ({ trackPageView }) => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location, trackPageView]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="home" element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="login" element={<Login />} />
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/smart-picks" element={<SmartPicks />} />
          <Route path="user" element={<User />}>
            <Route path=":userid" element={<User />} />
          </Route>
          <Route path="phd-vacancies" element={<Opportunities />} />
          <Route path="phd-vacancies/:category/:country?/:university?" element={<Opportunities />} />
          <Route path="phd-vacancies/:category/discipline/:discipline?" element={<Opportunities />} />
          <Route path="phd-vacancies/:category/discipline/:discipline?/country/:country?" element={<Opportunities />} />
          <Route path="phd-vacancies/:category/discipline/:discipline?/country/:country?/university/:university?" element={<Opportunities />} />
          <Route path="post-jobs" element={<OpportunityForm />} />
          <Route path="opportunities/:title" element={<OpportunityDetail />} />
          {/* Blog Routes */}
          <Route path="blogs" element={<BlogPage />} />
          <Route path="blog/:slug/:id" element={<BlogDetail />} />
          <Route path="blog/:id" element={<BlogDetail />} />
          <Route path="create-blog" element={<CreateBlog />} />
          <Route path="edit-blog/:id" element={<EditBlog />} />
          <Route path="embed-test" element={<EmbedTestPage />} />
          {/* University Routes */}
          <Route path="university/login" element={<UniversityLogin />} />
          <Route path="university/signup" element={<UniversitySignup />} />
          <Route path="university/verify" element={<UniversityEmailVerification />} />
          <Route path="university/forgot-password" element={<UniversityForgotPassword />} />
          <Route path="university/reset-password" element={<UniversityResetPassword />} />
          <Route path="university/dashboard" element={<UniversityDashboard />} />
          <Route path="university/applications/:opportunityId" element={<UniversityReviewApplicationsPage />} />
        </Route>
      </Routes>
    </>
  );
};

// SaaS Portal Routes (for subdomains like app.phdscanner.com, oxford.phdscanner.com)
const SaaSPortalRoutes: React.FC<AppRoutesProps> = ({ trackPageView }) => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location, trackPageView]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<SaaSDashboard />} />
        <Route path="login" element={<SaaSLogin />} />
        <Route path="dashboard" element={<SaaSDashboard />} />
        <Route path="review/:opportunityId" element={<SaasReviewApplicationsPage />} />
        {/* Redirect any other routes to dashboard */}
        <Route path="*" element={<SaaSDashboard />} />
      </Routes>
    </>
  );
};

// Development SaaS Routes (for localhost with /saas prefix)
const DevelopmentSaaSRoutes: React.FC<AppRoutesProps> = ({ trackPageView }) => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location, trackPageView]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/saas" element={<SaaSDashboard />} />
        <Route path="/saas/login" element={<SaaSLogin />} />
        <Route path="/saas/dashboard" element={<SaaSDashboard />} />
        <Route path="/saas/review/:opportunityId" element={<SaasReviewApplicationsPage />} />
        {/* Redirect any other saas routes to dashboard */}
        <Route path="/saas/*" element={<SaaSDashboard />} />
      </Routes>
    </>
  );
};

export const AppRoutes: React.FC<AppRoutesProps> = ({ trackPageView }) => {
  const hostname = window.location.hostname;
  
  // Determine which portal to show based on domain
  if (isSaaSSubdomain()) {
    // For localhost development with /saas prefix
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return <DevelopmentSaaSRoutes trackPageView={trackPageView} />;
    }
    // For production subdomains
    return <SaaSPortalRoutes trackPageView={trackPageView} />;
  } else {
    return <MainPortalRoutes trackPageView={trackPageView} />;
  }
};